import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, ArrowRightIcon, ChevronDownIcon, MapPinIcon, ShowerHeadIcon, BathIcon, ArrowUpIcon, SquareIcon, LayersIcon, HomeIcon, SunIcon, CookingPotIcon, FootprintsIcon, ThermometerIcon, PaintbrushIcon, WrenchIcon, ZapIcon, FenceIcon, SnowflakeIcon, DoorOpenIcon, WarehouseIcon, ShieldIcon, BugIcon, TreesIcon, SparklesIcon } from 'lucide-react';
import { services as allServices } from '../data/services';
const iconMap: Record<string, ComponentType<{
  className?: string;
}>> = {
  ShowerHeadIcon,
  BathIcon,
  ArrowUpIcon,
  SquareIcon,
  LayersIcon,
  HomeIcon,
  SunIcon,
  CookingPotIcon,
  FootprintsIcon,
  ThermometerIcon,
  PaintbrushIcon,
  WrenchIcon,
  ZapIcon,
  FenceIcon,
  SnowflakeIcon,
  DoorOpenIcon,
  WarehouseIcon,
  ShieldIcon,
  BugIcon,
  TreesIcon,
  SparklesIcon
};
function CostHeroForm({
  services,
  onSearch
}: {
  services: typeof allServices;
  onSearch: (service: string, zip: string) => void;
}) {
  const [selectedService, setSelectedService] = useState('');
  const [zip, setZip] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedService) {
      onSearch(selectedService, zip);
    }
  };
  return <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg" data-id="element-995">
      <div className="relative flex-1" data-id="element-996">
        <select value={selectedService} onChange={e => setSelectedService(e.target.value)} className="w-full h-14 pl-4 pr-10 bg-white border border-slate-300 rounded-xl text-slate-900 font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent cursor-pointer" data-id="element-997">
          <option value="" data-id="element-998">Select project</option>
          {services.map(s => <option key={s.slug} value={s.slug} data-id="element-999">
              {s.name}
            </option>)}
        </select>
        <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" data-id="element-1000" />
      </div>
      <div className="relative w-full sm:w-36" data-id="element-1001">
        <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" data-id="element-1002" />
        <input type="text" value={zip} onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="ZIP Code" maxLength={5} className="w-full h-14 pl-10 pr-4 bg-white border border-slate-300 rounded-xl text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent" data-id="element-1003" />
      </div>
      <button type="submit" disabled={!selectedService} className={`h-14 px-6 rounded-xl font-bold text-white transition-all whitespace-nowrap ${selectedService ? 'bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-500/25' : 'bg-slate-300 cursor-not-allowed'}`} data-id="element-1004">
        Let's find out
      </button>
    </form>;
}
interface CostGuidesProps {
  mode: 'list' | 'detail';
  serviceSlug?: string;
  onNavigate: (path: string) => void;
  onSearch?: (service: string, zip: string) => void;
}
export function CostGuides({
  mode,
  serviceSlug,
  onNavigate,
  onSearch
}: CostGuidesProps) {
  const services = allServices;
  if (mode === 'detail' && serviceSlug) {
    const service = services.find(s => s.slug === serviceSlug);
    if (!service) {
      return <div className="py-20 text-center" data-id="element-1005">Cost guide not found</div>;
    }
    return <div className="min-h-screen bg-slate-50" data-id="element-1006">
        <div className="bg-white py-4 border-b border-gray-100" data-id="element-1007">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-gray-500" data-id="element-1008">
            <button onClick={() => onNavigate('/')} className="hover:text-[#003DA5]" data-id="element-1009">
              Home
            </button>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1010" />
            <button onClick={() => onNavigate('/costs')} className="hover:text-[#003DA5]" data-id="element-1011">
              Cost Guides
            </button>
            <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1012" />
            <span className="text-gray-900 font-medium" data-id="element-1013">{service.name}</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-1014">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6" data-id="element-1015">
            How Much Does {service.name} Cost?
          </h1>
          <p className="text-xl text-slate-600 mb-12" data-id="element-1016">{service.description}</p>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-12" data-id="element-1017">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center" data-id="element-1018">
              Average Cost Range
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16" data-id="element-1019">
              <div className="text-center" data-id="element-1020">
                <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1021">
                  Low End
                </p>
                <p className="text-3xl font-bold text-slate-700" data-id="element-1022">
                  ${service.costRange.low.toLocaleString()}
                </p>
              </div>
              <div className="text-center bg-amber-50 rounded-xl p-6 border border-amber-100 relative" data-id="element-1023">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-1024">
                  NATIONAL AVERAGE
                </div>
                <p className="text-5xl font-black text-amber-600" data-id="element-1025">
                  ${service.costRange.average.toLocaleString()}
                </p>
              </div>
              <div className="text-center" data-id="element-1026">
                <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-1027">
                  High End
                </p>
                <p className="text-3xl font-bold text-slate-700" data-id="element-1028">
                  ${service.costRange.high.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-slate-100 text-center" data-id="element-1029">
              <button onClick={() => onNavigate(`/near-me/${service.slug}`)} className="inline-flex items-center justify-center h-14 px-8 bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg rounded-xl transition-colors shadow-lg shadow-amber-500/30" data-id="element-1030">
                Get Exact Quotes Near You
              </button>
            </div>
          </div>

          <div className="prose prose-lg prose-slate max-w-none" data-id="element-1031">
            <h3 data-id="element-1032">Factors That Affect {service.name} Costs</h3>
            <p data-id="element-1033">
              While the national average is $
              {service.costRange.average.toLocaleString()}, your actual cost
              will depend on several factors:
            </p>
            <ul data-id="element-1034">
              <li data-id="element-1035">
                <strong data-id="element-1036">Materials:</strong> Premium materials will push you
                toward the higher end of the range.
              </li>
              <li data-id="element-1037">
                <strong data-id="element-1038">Labor Rates:</strong> Contractor rates vary
                significantly by region and city.
              </li>
              <li data-id="element-1039">
                <strong data-id="element-1040">Project Size:</strong> Larger homes or more complex
                installations require more time and materials.
              </li>
              <li data-id="element-1041">
                <strong data-id="element-1042">Permits &amp; Fees:</strong> Local building codes may
                require permits that add to the total cost.
              </li>
            </ul>
          </div>
        </div>
      </div>;
  }
  // List Mode
  const handleHeroSearch = (service: string, zip: string) => {
    if (onSearch) {
      onSearch(service, zip);
    } else {
      onNavigate(`/near-me/${service}`);
    }
  };
  return <div className="min-h-screen bg-slate-50" data-id="element-1043">
      <div className="bg-white py-4 border-b border-gray-100" data-id="element-1044">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-gray-500" data-id="element-1045">
          <button onClick={() => onNavigate('/')} className="hover:text-[#003DA5]" data-id="element-1046">
            Home
          </button>
          <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1047" />
          <span className="text-gray-900 font-medium" data-id="element-1048">Cost Guides</span>
        </div>
      </div>

      <section className="bg-white overflow-hidden border-b border-slate-200" data-id="element-1049">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1050">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-1051">
            <div className="flex-1" data-id="element-1052">
              <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight" data-id="element-1053">
                How Much Does Home Improvement Really Cost?
              </h1>
              <p className="text-lg text-slate-600 mb-8" data-id="element-1054">
                Every renovation starts with one big question: how much will it
                cost? We've analyzed thousands of home improvement projects to
                help you estimate prices before you hire or start.
              </p>
              <ul className="space-y-3 text-slate-700 font-medium mb-8" data-id="element-1055">
                <li className="flex items-center gap-2" data-id="element-1056">
                  <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1057"></div>
                  Over 20 detailed cost guides
                </li>
                <li className="flex items-center gap-2" data-id="element-1058">
                  <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1059"></div>
                  Trusted by thousands of homeowners
                </li>
              </ul>

              <CostHeroForm services={services} onSearch={handleHeroSearch} data-id="element-1060" />
              <p className="text-xs text-slate-400 mt-3" data-id="element-1061">
                No-obligation estimates.
              </p>
            </div>
            <div className="flex-1 w-full" data-id="element-1062">
              <img src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80" alt="Beautiful home exterior" className="rounded-3xl shadow-xl w-full h-[400px] object-cover" data-id="element-1063" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-id="element-1064">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1065">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12" data-id="element-1066">
            Project Categories
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1067">
            {services.map((service, idx) => {
            const IconComp = iconMap[service.iconName];
            return <button key={idx} onClick={() => onNavigate(`/costs/${service.slug}`)} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-lg hover:border-amber-300 hover:-translate-y-1 transition-all duration-200 text-left group flex items-center gap-5" data-id="element-1068">
                  <div className="w-14 h-14 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500 group-hover:border-amber-500 transition-colors" data-id="element-1069">
                    {IconComp && <IconComp className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" data-id="element-1070" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1071">
                    <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-slate-900 transition-colors" data-id="element-1072">
                      {service.name}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-1073">
                      ${service.costRange.low.toLocaleString()} – $
                      {service.costRange.high.toLocaleString()}
                    </p>
                    <p className="text-xs font-semibold text-amber-600 mt-1" data-id="element-1074">
                      Avg: ${service.costRange.average.toLocaleString()}
                    </p>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition-all transform group-hover:translate-x-1 flex-shrink-0" data-id="element-1075" />
                </button>;
          })}
          </div>
        </div>
      </section>
    </div>;
}